import { useEffect, useState } from "react";
import { PencilSimple, Plus, SpinnerGap, Trash, X } from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";
import { CONTACT_ROLES, roleByKey } from "@/lib/constants";
import { toast } from "@/components/ui/sonner";

const EMPTY = { role: "conseil", name: "", detail: "", phone: "", email: "" };

export default function AdminContacts() {
  const [contacts, setContacts] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/contacts").then((r) => setContacts(r.data)).catch(() => setContacts([]));
  }, []);

  const payload = () => ({
    role: form.role,
    name: form.name,
    detail: form.detail || null,
    phone: form.phone || null,
    email: form.email || null,
  });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Le nom est requis");
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        const { data } = await api.patch(`/admin/contacts/${editingId}`, payload());
        setContacts((list) => list.map((c) => (c.id === editingId ? data : c)));
        toast.success(`${data.name} mis(e) à jour`);
      } else {
        const { data } = await api.post("/admin/contacts", payload());
        setContacts((c) => [...(c || []), data]);
        toast.success(`${data.name} ajouté(e)`);
      }
      setForm(EMPTY);
      setEditingId(null);
    } catch (err) {
      toast.error(formatApiError(err));
    }
    setSaving(false);
  };

  const startEdit = (c) => {
    setEditingId(c.id);
    setForm({
      role: c.role,
      name: c.name,
      detail: c.detail || "",
      phone: c.phone || "",
      email: c.email || "",
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(EMPTY);
  };

  const remove = async (c) => {
    try {
      await api.delete(`/admin/contacts/${c.id}`);
      setContacts((list) => list.filter((x) => x.id !== c.id));
      if (editingId === c.id) cancelEdit();
      toast.success(`${c.name} retiré(e)`);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const inputCls =
    "w-full rounded-2xl border border-black/10 px-4 py-3 text-sm outline-none transition-colors focus:border-primary";

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_1.2fr]" data-testid="admin-contacts-tab">
      <form onSubmit={submit} className="h-fit rounded-3xl border border-black/5 bg-white p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
        <h2 className="font-display text-lg font-bold tracking-tight">
          {editingId ? "Modifier la personne" : "Ajouter une personne"}
        </h2>
        <p className="mt-1 text-xs text-muted-foreground">Visible par tous les résidents sur la page Informations.</p>
        <div className="mt-5 space-y-3">
          <select
            value={form.role}
            onChange={(e) => setForm({ ...form, role: e.target.value })}
            data-testid="contact-role-select"
            className={inputCls}
          >
            {CONTACT_ROLES.map((r) => (
              <option key={r.key} value={r.key} data-testid={`contact-role-${r.key}`}>{r.label}</option>
            ))}
          </select>
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nom (ex. Karim L.)" data-testid="contact-name-input" className={inputCls} />
          <input value={form.detail} onChange={(e) => setForm({ ...form, detail: e.target.value })} placeholder="Fonction (ex. Président du conseil syndical)" data-testid="contact-detail-input" className={inputCls} />
          <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Téléphone (optionnel)" data-testid="contact-phone-input" className={inputCls} />
          <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email (optionnel)" data-testid="contact-email-input" className={inputCls} />
        </div>
        <div className="mt-5 flex gap-2">
          <button
            type="submit"
            disabled={saving}
            data-testid="contact-add-button"
            className="flex flex-1 items-center justify-center gap-2 rounded-full bg-primary px-6 py-3.5 text-sm font-bold text-primary-foreground transition-colors hover:bg-[#265442] disabled:opacity-60"
          >
            {saving ? <SpinnerGap size={16} className="animate-spin" /> : editingId ? <PencilSimple size={16} weight="bold" /> : <Plus size={16} weight="bold" />}
            {editingId ? "Enregistrer les modifications" : "Ajouter la personne"}
          </button>
          {editingId && (
            <button
              type="button"
              onClick={cancelEdit}
              data-testid="contact-cancel-edit"
              className="inline-flex items-center gap-1.5 rounded-full border border-black/10 px-5 py-3.5 text-sm font-bold transition-colors hover:border-primary hover:text-primary"
            >
              <X size={14} weight="bold" /> Annuler
            </button>
          )}
        </div>
      </form>

      <div className="space-y-3" data-testid="admin-contacts-list">
        {contacts === null && <div className="h-24 animate-pulse rounded-3xl bg-black/5" />}
        {contacts !== null && contacts.length === 0 && (
          <p className="rounded-3xl border border-black/5 bg-white p-8 text-center text-sm text-muted-foreground">
            Aucune personne. Ajoutez le gestionnaire, le concierge et le conseil syndical.
          </p>
        )}
        {contacts?.map((c) => {
          const { Icon, label } = roleByKey(c.role);
          return (
            <div key={c.id} className="flex items-center gap-4 rounded-3xl border border-black/5 bg-white p-4 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid={`admin-contact-row-${c.id}`}>
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                <Icon size={18} weight="duotone" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-bold">
                  {c.name} <span className="ml-1 rounded-full bg-secondary px-2 py-0.5 text-[10px] font-bold text-secondary-foreground">{label}</span>
                </p>
                <p className="truncate text-xs text-muted-foreground">
                  {[c.detail, c.phone, c.email].filter(Boolean).join(" · ") || "—"}
                </p>
              </div>
              <button
                onClick={() => startEdit(c)}
                data-testid={`contact-edit-${c.id}`}
                aria-label={`Modifier ${c.name}`}
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-black/10 text-foreground/50 transition-colors hover:border-primary hover:text-primary"
              >
                <PencilSimple size={16} weight="duotone" />
              </button>
              <button
                onClick={() => remove(c)}
                data-testid={`contact-delete-${c.id}`}
                aria-label={`Retirer ${c.name}`}
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-black/10 text-foreground/50 transition-colors hover:border-[#C90A0A] hover:text-[#C90A0A]"
              >
                <Trash size={16} weight="duotone" />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
